import { useEffect, useState, useCallback } from 'react';
import {
	getFirestore,
	getAggregateFromServer,
	collection,
	where,
	orderBy,
	startAt,
	limit,
	doc,
	getDocs,
	query,
	count,
	sum,
	getCountFromServer
} from 'firebase/firestore';
import { useDocumentDataOnce } from 'react-firebase-hooks/firestore';
import { getPreviousPeriod } from '../utils/time';

const getQuery = (shopId, range, statusOptions, hostId = null,cursor) => {
	const firestore = getFirestore();
	let q = collection(firestore, 'chatRoomSessions');
	q = query(q, where('shopId', '==', shopId));	
	if (hostId) {
		q = query(q, where('hostId', '==', hostId));
	}
	q = query(q, where('status', 'in', statusOptions));

	if (range) {
		const start = range.start.set({ hour: 0, minutes: 0, seconds: 0 }).toDate()
		const end = range.end.set({ hour: 23, minutes: 59, seconds: 59 }).toDate()
		q = query(q, where('createdAt', '<=', end));
		q = query(q, where('createdAt', '>=', start));
	}
	q = query(q, orderBy('createdAt', 'desc'));

	if (cursor) {
		q = query(q, startAt(cursor));
	}
	return q;
}

export default function useChatSessions(shopId, statusOptions, rowsCount, filter) {
	const [sessions, setSessions] = useState([]);
	const [loading, setLoading] = useState(false)
	const [getNextSessions, setNextSessions] = useState(null);
	const { range, hostId = null } = filter;

	const getSessions = async (shopId, doclimit, cursor = null) => {
		setLoading(true)
		const q = getQuery(shopId, range, statusOptions, hostId, cursor);
		const querySnap = await getDocs(query(q, limit(doclimit + 1)));
		let docs = querySnap.docs;
		let next = null;
		if (querySnap.size > doclimit) {
			const nextCursor = docs.pop();
			next = () => getSessions(shopId, doclimit, nextCursor);
		}
		return { docs, next };
	}

	useEffect(() => {
		getSessions(shopId, rowsCount * 2, null).then(({ docs, next }) => {
			setSessions([...sessions, ...docs]);
			setNextSessions(next);
			setLoading(false)
		});
	}, [])

	const refresh = () => {
		getSessions(shopId, rowsCount * 2, null).then(({ docs, next }) => {
			setSessions([...docs]);
			setNextSessions(next);
			setLoading(false)
		});
	};

	const getMoreSessions = () => {
		if (getNextSessions) {
			getNextSessions().then(({ docs, next }) => {
				setSessions([...sessions, ...docs]);
				setNextSessions(next);
			});
		}
	}

	return {
		sessions,
		getSessions,
		getMoreSessions,
		refresh,
		loading
	};
}

export function useChatSessionData(sessionId) {
	const firestore = getFirestore();
	const ref = sessionId ? doc(firestore, 'chatRoomSessions', sessionId) : null;
	const [sessionData, sessionDataLoading, sessionDataError] = useDocumentDataOnce(ref);
	return {
		sessionData, sessionDataLoading, sessionDataError,
	}
}

export function useChatSessionAggregates(shopId, statusOptions, filter) {
	const [aggregates, setAggregates] = useState(null);
	const [loading, setLoading] = useState(false)


	const getSessionsAggregates = async () => {		
		setLoading(true)
		try {
			const previousPeriod = getPreviousPeriod(filter.range.start, filter.range.end);			
			const q = getQuery(shopId, filter.range, statusOptions, filter.hostId);
			const previousQ = getQuery(shopId, previousPeriod, statusOptions, filter.hostId);

			const totalSessions = await getAggregateFromServer(q,
				{
					totalSessions: count(),
					totalMessages: sum('messageCounter'),

				}
			);
			const ordersAgg = await getAggregateFromServer(q,
				{
					totalOrders: count('totalOrderAmount'),
					totalOrderAmount: sum('totalOrderAmount'),
				}
			);

			const totalSessionsPrevious = await getAggregateFromServer(previousQ,
				{
					totalSessionsPrevious: count(),
					totalMessagesPrevious: sum('messageCounter'),

				}
			);
			const previousOrdersAgg = await getAggregateFromServer(previousQ,
				{
					totalOrdersPrevious: count('totalOrderAmount'),
					totalOrderAmountPrevious: sum('totalOrderAmount'),
				}
			);
			return {
				...totalSessions.data(),
				...ordersAgg.data(),
				...totalSessionsPrevious.data(),
				...previousOrdersAgg.data()
			}
		} catch (e) {
			console.error(e)
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		getSessionsAggregates().then(setAggregates)
	}, [shopId, filter]);

	return {
		aggregates, loading
	}

}